/**
 * Discriminating questions (ADR-033).
 *
 * When resolution escalates, the obvious move is to show the candidate list and
 * ask the person to pick. That move is an apology: it shows our uncertainty,
 * names occupations in a vocabulary they have never used, and asks them to
 * repair our failure. It also collects nothing — once they pick, the answer is
 * spent.
 *
 * This module asks a different question. Not "which of these titles are you?"
 * but "which of these things do you actually do?" — chosen so that each answer
 * splits the remaining candidates as evenly as possible, which is the question
 * that carries the most information per unit of the person's attention.
 *
 * Three properties make it a feature rather than a tax:
 *
 *   1. It asks about WORK, in work language. Detailed Work Activities are
 *      O*NET's domain-neutral phrasing — "Manage information technology
 *      projects or system activities" — so the question is answerable by anyone
 *      who has done the job, and unanswerable by our own uncertainty.
 *
 *   2. It never asks what cannot change the answer. A DWA shared by every
 *      remaining candidate, or by none, has zero information gain and is never
 *      shown. Questions stop the moment confidence clears the bar rather than
 *      running a fixed-length quiz.
 *
 *   3. The answers outlive the question. What comes back is this person's own
 *      task set — which is the input the exposure score has been missing.
 *      Until now every holder of a given occupation code received an identical
 *      score, because we scored the OCCUPATION's tasks. Two IT project managers
 *      do different work; after this they get different numbers. The refinement
 *      is not the toll gate before the product. It is the only step that makes
 *      the product personal.
 */

/** Shannon entropy of a weight distribution, in bits. */
function entropy(weights) {
  const total = weights.reduce((s, w) => s + w, 0);
  if (total <= 0) return 0;
  let h = 0;
  for (const w of weights) {
    const p = w / total;
    if (p > 0) h -= p * Math.log2(p);
  }
  return h;
}

/**
 * Expected entropy after asking about one activity.
 *
 * The person answers yes or no. We do not know which, so we weight both
 * outcomes by how likely they are given what we currently believe. A question
 * that splits the candidates 50/50 leaves the least uncertainty whichever way
 * it is answered — which is precisely the question worth asking.
 */
function expectedEntropy(candidates, hasActivity) {
  const yes = candidates.filter((c) => hasActivity(c));
  const no = candidates.filter((c) => !hasActivity(c));
  const total = candidates.reduce((s, c) => s + c.weight, 0) || 1;
  const wYes = yes.reduce((s, c) => s + c.weight, 0) / total;
  const wNo = no.reduce((s, c) => s + c.weight, 0) / total;
  return wYes * entropy(yes.map((c) => c.weight)) + wNo * entropy(no.map((c) => c.weight));
}

/**
 * Build the question set for a candidate list.
 *
 * @param {Array<{code,title,confidence}>} matches   from resolveWithContext
 * @param {object} dwaIndex   dwas.json — code -> [activity]
 * @param {number} limit      how many questions to prepare
 */
export function buildQuestions(matches, dwaIndex, { limit = 4 } = {}) {
  const candidates = (matches || []).slice(0, 6).map((m) => ({
    code: m.code,
    title: m.title,
    weight: Math.max(0.05, m.confidence || 0.1),
    activities: new Set(dwaIndex[m.code] || []),
  }));
  if (candidates.length < 2) return [];

  // An activity is only worth asking about if it separates the field. Present in
  // all of them, or none, it tells us nothing however true it is — and asking an
  // unfalsifiable question is how a form makes someone feel processed.
  const counts = new Map();
  for (const c of candidates) {
    for (const a of c.activities) counts.set(a, (counts.get(a) || 0) + 1);
  }
  const splitting = [...counts.entries()]
    .filter(([, n]) => n >= 1 && n < candidates.length)
    .map(([activity]) => activity);

  const questions = [];
  let live = candidates;
  const asked = new Set();

  for (let i = 0; i < limit; i++) {
    const before = entropy(live.map((c) => c.weight));
    if (before <= 0.01) break;               // one candidate left; stop asking

    let best = null;
    for (const activity of splitting) {
      if (asked.has(activity)) continue;
      const has = (c) => c.activities.has(activity);
      const supporters = live.filter(has);
      if (!supporters.length || supporters.length === live.length) continue;
      const gain = before - expectedEntropy(live, has);
      if (!best || gain > best.gain) {
        best = { activity, gain, supports: supporters.map((c) => c.code) };
      }
    }
    if (!best || best.gain <= 0.01) break;

    asked.add(best.activity);
    questions.push({
      id: `q${i + 1}`,
      activity: best.activity,
      gainBits: Math.round(best.gain * 100) / 100,
      supports: best.supports,
      excludes: live.filter((c) => !c.activities.has(best.activity)).map((c) => c.code),
    });

    // Assume the more likely answer for the purpose of choosing the NEXT
    // question, so the set we prepare is not four variations on one split.
    const totalW = live.reduce((s, c) => s + c.weight, 0) || 1;
    const yesW = live.filter((c) => c.activities.has(best.activity))
      .reduce((s, c) => s + c.weight, 0) / totalW;
    live = live.filter((c) => (yesW >= 0.5 ? c.activities.has(best.activity) : true));
    if (live.length < 2) break;
  }

  return questions;
}

/**
 * Fold answers back into the ranking.
 *
 * The naive version multiplied a penalty into every candidate that HAD an
 * activity the person denied, and left the rest untouched. That quietly rewards
 * ignorance: a candidate sharing nothing with any question takes no penalty at
 * all and floats to the top. Simulating a run of "no" answers for a technology
 * programme manager promoted *Media Programming Directors* — the one occupation
 * in the list that the questions never touched.
 *
 * So all four cases are scored, not two:
 *
 *   has it,  said yes   →  strong agreement. This is the evidence we asked for.
 *   has it,  said no    →  disagreement, and it counts nearly as hard.
 *   lacks it, said yes  →  the person does work this occupation does not cover.
 *   lacks it, said no   →  weak agreement — true of most occupations, so cheap.
 *
 * The asymmetry that remains is deliberate. People under-report: a résumé omits
 * half of what someone did, and a person answering quickly says no to work they
 * do occasionally. Confirmation is therefore worth more than denial costs.
 */
const AGREEMENT = {
  hasYes: 1.0,
  hasNo: -0.8,
  lacksYes: -0.5,
  // Nearly zero on purpose. "You don't do X and this occupation doesn't involve
  // X" is true of most of the dataset, so paying for it hands a bonus to
  // whichever candidate the questions never touched — which is exactly how a
  // run of "no" answers promoted the one occupation nobody had asked about.
  lacksNo: 0.05,
};

export function applyAnswers(matches, answers, dwaIndex) {
  const entries = Object.entries(answers || {});
  if (!entries.length) return matches;

  const scored = matches.map((m) => {
    const acts = new Set(dwaIndex[m.code] || []);
    let agreement = 0;
    // Keep the ACTIVITIES, not just tallies. A number that moved is not an
    // explanation; "it rose because you confirmed X and ruled out Y" is. The UI
    // cannot reconstruct this afterwards, so it has to come out of the scorer.
    const helped = [], hurt = [], missing = [];
    for (const [activity, said] of entries) {
      const has = acts.has(activity);
      if (has && said) { agreement += AGREEMENT.hasYes; helped.push(activity); }
      else if (has && !said) { agreement += AGREEMENT.hasNo; hurt.push(activity); }
      else if (!has && said) { agreement += AGREEMENT.lacksYes; missing.push(activity); }
      else agreement += AGREEMENT.lacksNo;
    }
    return {
      ...m, agreement, prior: m.confidence || 0.1,
      confirmed: helped.length, denied: hurt.length,
      why: { helped, hurt, missing },
    };
  });

  // Relative standing from prior x evidence, then an ABSOLUTE ceiling set by how
  // much the person actually told us. Four questions is real evidence; it is not
  // certainty, and a number like 0.97 after four taps would be the same
  // overclaiming this resolver already had to be talked out of once.
  //
  // The ceiling counts YES answers only. Denial narrows the field; it does not
  // locate anyone. Someone who says no to everything has told us where they are
  // not, and the honest response to that is to stay unsure and keep asking —
  // not to award 0.9 to whatever is left standing.
  const yesCount = entries.filter(([, said]) => said).length;
  const ceiling = Math.min(0.9, 0.5 + 0.1 * yesCount);

  const weighted = scored.map((c) => ({ ...c, raw: c.prior * Math.exp(0.75 * c.agreement) }));
  const top = Math.max(...weighted.map((c) => c.raw), 1e-6);

  const after = weighted
    .map((c) => ({ ...c, confidence: Math.round(ceiling * (c.raw / top) * 100) / 100 }))
    .sort((a, b) => b.confidence - a.confidence);

  // Movement, computed here rather than in the view so "what changed" is a
  // property of the result and not something the screen has to infer.
  const beforeRank = new Map(matches.map((m, i) => [m.code, i]));
  return after.map((c, i) => ({
    ...c,
    rankBefore: beforeRank.has(c.code) ? beforeRank.get(c.code) : null,
    rankAfter: i,
    moved: beforeRank.has(c.code) ? beforeRank.get(c.code) - i : 0,
    confidenceBefore: c.prior,
  }));
}

/**
 * The person's own task set, assembled from what they confirmed.
 *
 * This is the output that makes the whole exchange worth having: the exposure
 * scorer stops reading the occupation's generic task list and reads this.
 */
export function confirmedActivities(answers) {
  return Object.entries(answers || {}).filter(([, said]) => said).map(([a]) => a);
}

/**
 * Answer what we can before asking anything (ADR-025, ADR-033).
 *
 * The standing principle on this product is that user input is the LAST rung of
 * the ladder, not the first — collecting agentically from what we already hold
 * is how the system earns the claim to be AI-first. A question whose answer is
 * sitting in the résumé is not a question; it is us making the person do our
 * reading.
 *
 * So every prepared question is first put to the document. Where the activity's
 * content words appear in what the person already wrote about their own work,
 * we answer it ourselves and mark it inferred. Only the residue is shown.
 *
 * Inferred answers are held to a HIGHER bar than asked ones and are always
 * overridable: being wrong on the person's behalf, silently, is worse than
 * asking. Absence is never read as denial — a résumé omits most of a job, so
 * "not mentioned" means unknown, and unknown is what we ask about.
 */
const ACTIVITY_STOP = new Set([
  "with", "others", "and", "the", "for", "such", "that", "their", "this", "from",
  "determine", "ensure", "other", "using", "within", "about", "into", "over",
]);

export function preAnswer(questions, parsed, { minHits = 2 } = {}) {
  const hay = ` ${[parsed?.taskText || "", (parsed?.skills || []).join(" "),
    (parsed?.roles || []).map((r) => r.title).join(" ")].join(" ").toLowerCase()} `;

  const inferred = {};
  const ask = [];
  for (const q of questions) {
    const words = q.activity.toLowerCase().replace(/[^a-z ]/g, " ").split(/\s+/)
      .filter((w) => w.length > 4 && !ACTIVITY_STOP.has(w));
    // Stem-prefix, for the same reason every other matcher here uses one:
    // documents say "managing", "projects", "developed".
    const hits = words.filter((w) => hay.includes(` ${w.slice(0, Math.max(5, w.length - 2))}`)).length;
    if (hits >= minHits && words.length >= 2) inferred[q.activity] = true;
    else ask.push(q);
  }
  return { inferred, ask, inferredCount: Object.keys(inferred).length };
}
